<script setup lang="ts">
import { IonContent } from '@ionic/vue';
import type { InfiniteScrollCustomEvent, RefresherCustomEvent } from '@ionic/vue';

type SegmentKey = 'all'| 'unread';

interface NotificationSegmentButton {
  value: SegmentKey;
  label: string;
}
const selectedSegment = ref<SegmentKey>('all');
const segmentOptions = computed<NotificationSegmentButton[]>(() => [
  { value: 'all', label: t('notification.all') },
  { value: 'unread', label: t('notification.unread') }
]);
const { t } = useEkI18n();
const platformStore = usePlatformStore();

const notificationStore = useNotificationStore();
const { allNotifications, unreadNotifications, isFinalPage } = storeToRefs(notificationStore);
const pushNotificationApi = usePushNotificationApi();
const tokenStore = useTokenStore();
const displayedNotifications = computed(() =>
  selectedSegment.value === 'unread' ? unreadNotifications.value : allNotifications.value
);

const userConfigStore = useUserConfigurationStore();
const router = useRouting();

const currentPage = ref(1);
const PAGE_SIZE = 50;
const isRefreshing = ref(false);
const notificationContent = useTemplateRef<InstanceType<typeof IonContent>>('notificationContent');
const infiniteScrollKey = ref(0);

watch(() => router.route.path, () => {
  if (!userConfigStore.userConfiguration?.IsWebUser) {
    currentPage.value = 1;
    notificationStore.getPushNotifications({ UnreadNotificationsOnly: false, PageSize: PAGE_SIZE, Page: 1 });
  }
}, { immediate: true });

async function loadMore(event: InfiniteScrollCustomEvent) {
  currentPage.value++;
  await notificationStore.appendPushNotifications({ UnreadNotificationsOnly: false, PageSize: PAGE_SIZE, Page: currentPage.value });
  await event.target.complete();
}

async function refresh() {
  currentPage.value = 1;
  isRefreshing.value = true;

  try {
    await notificationStore.getPushNotifications({ UnreadNotificationsOnly: false, PageSize: PAGE_SIZE, Page: 1 });
    infiniteScrollKey.value++;
    await notificationContent.value?.$el.scrollToTop(0);
  } finally {
    isRefreshing.value = false;
  }
}

async function handleRefresh(event: RefresherCustomEvent) {
  await refresh();
  event.target.complete();
}

async function markAllAsRead() {
  await notificationStore.markAllAsRead();
}

async function markNotificationsAsAcknowledged() {
  if (allNotifications.value.length === 0) return;

  const userId = await tokenStore.getUserID();
  await pushNotificationApi.markNotificationAsAcknowledged({ NotificationIds: allNotifications.value.map(n => n.NotificationID), UserId: userId });
}

</script>

<template>
  <IonMenu
    menu-id="notification-menu"
    content-id="main-content"
    :swipe-gesture="false"
    side="end"
    @ion-did-open="markNotificationsAsAcknowledged"
  >
    <IonHeader>
      <IonToolbar>
        <IonTitle>{{ t('notification.title') }}</IonTitle>
        <ButtonsToolbar
          v-if="platformStore.isWeb"
          slot="end"
          v-tooltip.top="t('buttons.update')"
          icon="fa-light fa-arrow-rotate-right"
          :loading="isRefreshing"
          @click="refresh()"
        />
      </IonToolbar>
      <IonSegment v-model="selectedSegment">
        <IonSegmentButton
          v-for="option in segmentOptions"
          :key="option.value"
          :value="option.value"
        >
          <IonLabel>{{ option.label }}</IonLabel>
        </IonSegmentButton>
      </IonSegment>
    </IonHeader>
    <IonContent ref="notificationContent">
      <IonRefresher
        slot="fixed"
        :disabled="platformStore.isWeb"
        @ion-refresh="handleRefresh($event)"
      >
        <IonRefresherContent />
      </IonRefresher>
      <NotificationPreview
        v-for="(notification, index) in displayedNotifications"
        :key="index"
        :notification="notification"
      />
      <IonInfiniteScroll
        :key="infiniteScrollKey"
        :disabled="isFinalPage"
        @ion-infinite="loadMore"
      >
        <IonInfiniteScrollContent />
      </IonInfiniteScroll>
    </IonContent>
    <IonFooter>
      <IonButton
        expand="block"
        shape="round"
        class="ion-margin"
        @click="markAllAsRead"
      >
        <IonLabel class="ion-text-center">
          {{ t('notification.readAll') }}
        </IonLabel>
      </IonButton>
    </IonFooter>
  </IonMenu>
</template>

<style scoped>
ion-menu {
  --width: 16em;
}
</style>
